import React, { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * What a registered submit handler may resolve to.
 *
 * JourneyPage registers `(handler: () => Promise<boolean>)` and blocks Continue
 * on a falsy result, while some older hosts expect the `{ isValid }` object. The
 * union keeps this component compatible with both instead of forcing one caller
 * to be wrong — `false` is what JourneyPage actually acts on.
 */
type SubmitResult = boolean | { isValid: boolean; error?: string };

interface BusinessConstitutionInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { business_constitution_type?: string | null };
  onChange?: (val: { business_constitution_type: string | null }) => void;
  disabled?: boolean;
  registerSubmit?: (fn: () => Promise<SubmitResult>) => void;
  // Legacy pattern
  initialData?: { business_constitution_type?: string };
  onDataChange?: (data: { business_constitution_type: string | null }) => void;
}

const BusinessConstitutionInput: React.FC<BusinessConstitutionInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const currentValue = (value?.business_constitution_type ?? initialData?.business_constitution_type ?? null);
  const isSoleProp = currentValue === 'soleProprietorship';

  // Opened when Continue is pressed with the box unticked. A dialog (not an
  // inline note or a toast) because the user has just tried to leave the page:
  // it has to interrupt, and it must stay on screen until they dismiss it.
  const [showDialog, setShowDialog] = React.useState(false);

  const handleChange = useCallback((checked: boolean) => {
    const newValue = checked ? 'soleProprietorship' : null;
    onChange?.({ business_constitution_type: newValue });
    onDataChange?.({ business_constitution_type: newValue });
  }, [onChange, onDataChange]);

  // Continue-time gate.
  //
  // WHY BLOCK AT ALL: this journey only supports sole proprietors. Leaving the
  // box unticked used to let the user walk several pages further and fail later
  // in verification, with no hint that this checkbox was the cause.
  //
  // WHY A HANDLER RATHER THAN `required_fields`: the journey config deliberately
  // omits business_constitution_type from required_fields (see
  // create_caprion_journey.py) — that would grey the Continue button out with no
  // explanation. Returning false here keeps the button live and lets us say WHY.
  //
  // `registerSubmit` in JourneyPage is typed `() => Promise<boolean>` and treats
  // a falsy result as "stay on this page", so this returns a bare boolean. The
  // `{isValid}` shape in the props type is the legacy contract and is not what
  // JourneyPage reads.
  React.useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      if (isSoleProp) return true;
      setShowDialog(true);
      return false;
    });
    // `isSoleProp` must be a dependency: the handler is captured by value, so
    // without it the gate would keep using the checkbox state from first render
    // and reject even after the user ticks the box.
  }, [registerSubmit, isSoleProp]);

  return (
    <div className="space-y-2">
      <Label className="text-gray-500 font-medium">
        Constitution
      </Label>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="sole-proprietorship"
          checked={isSoleProp}
          onCheckedChange={handleChange}
          disabled={disabled}
        />
        <label
          htmlFor="sole-proprietorship"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Sole Proprietorship
        </label>
      </div>

      {/* DialogContent ships its own X close button in the top-right corner. */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            {/* Radix requires a DialogTitle for the dialog to be announced to
                screen readers — omitting it logs an a11y warning and leaves the
                popup unlabelled. Kept in the DOM but visually hidden, so the
                message below is the only thing on screen. */}
            <DialogTitle className="sr-only">Sole Proprietorship required</DialogTitle>
            <DialogDescription className="pt-2 text-base font-bold text-gray-900">
              This loan is only for sole proprietorship businesses. If your
              business is a sole proprietorship, tick the box and continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={() => setShowDialog(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessConstitutionInput;
